import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSearch, FaDownload, FaFileInvoice, FaCheckCircle,
  FaClock, FaTimesCircle, FaEye, FaTimes, FaCalendarAlt,
  FaUser, FaEnvelope, FaPhone, FaBriefcase, FaGraduationCap,
  FaMapMarkerAlt, FaCommentAlt, FaPaperPlane, FaUsers,
  FaExclamationCircle, FaHourglassHalf, FaThumbsUp,
  FaFilePdf, FaSpinner, FaEuroSign, FaCheck,
} from 'react-icons/fa';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Navbar  from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';

import { API_URL as API } from '../config/api';
const SB_OPEN = 252; const SB_CLOSED = 74; const SB_GAP = 12;
const NB_HEIGHT = 68; const NB_TOP = 12;

// ── Toast maison ──────────────────────────────────────────────────────────────
const Toast = ({ toasts, remove }) => (
  <div style={{ position:'fixed', bottom:24, right:24, zIndex:9999, display:'flex', flexDirection:'column', gap:10 }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        display:'flex', alignItems:'center', gap:12,
        padding:'14px 18px', borderRadius:14, minWidth:300, maxWidth:380,
        background: t.type==='success' ? 'linear-gradient(135deg,#0d1f14,#071a10)'
                  : t.type==='error'   ? 'linear-gradient(135deg,#1f0d0d,#1a0707)'
                  : 'linear-gradient(135deg,#0d1520,#071020)',
        border: `1px solid ${t.type==='success'?'rgba(25,135,84,0.4)':t.type==='error'?'rgba(220,53,69,0.4)':'rgba(253,126,20,0.4)'}`,
        boxShadow:'0 16px 48px rgba(0,0,0,0.4)',
        animation:'toast-in 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        <div style={{ fontSize:16, flexShrink:0,
          color: t.type==='success'?'#20c997':t.type==='error'?'#dc3545':'#fd7e14' }}>
          {t.type==='success'?<FaCheckCircle/>:t.type==='error'?<FaTimesCircle/>:<FaClock/>}
        </div>
        <p style={{ flex:1, fontSize:14, fontWeight:500, color:'#eef2f7', margin:0,
          fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{t.msg}</p>
        <button onClick={()=>remove(t.id)} style={{ background:'none', border:'none', color:'rgba(200,212,224,0.4)', cursor:'pointer', fontSize:12 }}>✕</button>
      </div>
    ))}
    <style>{`@keyframes toast-in{from{opacity:0;transform:translateY(16px) scale(0.95)}to{opacity:1;transform:none}}`}</style>
  </div>
);

const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type='success') => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
  }, []);
  const remove = useCallback(id => setToasts(p => p.filter(t => t.id !== id)), []);
  return { toasts, add, remove };
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Sk = ({ h=120, r=14 }) => (
  <div style={{ height:h, borderRadius:r,
    background:'linear-gradient(90deg,rgba(128,128,128,0.08) 25%,rgba(128,128,128,0.15) 50%,rgba(128,128,128,0.08) 75%)',
    backgroundSize:'200% 100%', animation:'dv-shimmer 1.4s infinite' }} />
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUTS = {
  nouveau:        { label:'Nouveau',           color:'#8fa3b5', bg:'rgba(143,163,181,0.1)', border:'rgba(143,163,181,0.25)', icon:FaFileInvoice },
  en_attente:     { label:'En attente',        color:'#fd7e14', bg:'rgba(253,126,20,0.1)',  border:'rgba(253,126,20,0.25)',  icon:FaClock        },
  en_traitement:  { label:'En traitement',     color:'#20c997', bg:'rgba(32,201,151,0.1)', border:'rgba(32,201,151,0.25)', icon:FaHourglassHalf },
  en_approbation: { label:'En approbation',    color:'#6f42c1', bg:'rgba(111,66,193,0.1)', border:'rgba(111,66,193,0.25)', icon:FaHourglassHalf },
  approuve:       { label:'Approuvé',          color:'#198754', bg:'rgba(25,135,84,0.1)',   border:'rgba(25,135,84,0.25)',  icon:FaCheckCircle  },
  envoye:         { label:'Envoyé',            color:'#198754', bg:'rgba(25,135,84,0.08)',  border:'rgba(25,135,84,0.2)',   icon:FaPaperPlane   },
  rejete:         { label:'Rejeté',            color:'#dc3545', bg:'rgba(220,53,69,0.1)',   border:'rgba(220,53,69,0.25)',  icon:FaTimesCircle  },
};

const TYPE_LABELS = { presentiel:'Présentiel', elearning:'E-Learning', blended:'Blended' };
const TYPE_COLORS = {
  presentiel:{ color:'#198754', bg:'rgba(25,135,84,0.1)',  border:'rgba(25,135,84,0.2)'  },
  elearning: { color:'#20c997', bg:'rgba(32,201,151,0.1)', border:'rgba(32,201,151,0.2)' },
  blended:   { color:'#fd7e14', bg:'rgba(253,126,20,0.1)', border:'rgba(253,126,20,0.2)' },
};

const fmtDate   = d => d ? new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtMoney  = n => n != null ? Number(n).toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2})+' €' : '—';

// ── Devis.jsx principal ───────────────────────────────────────────────────────
const Devis = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { toasts, add: addToast, remove: removeToast } = useToasts();

  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isSuperAdmin = user?.role === 'super_admin';
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  // ── State ─────────────────────────────────────────────────────────────────
  const [devisList, setDevisList]   = useState([]);
  const [stats, setStats]           = useState({});
  const [loading, setLoading]       = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [currentPage, setCurrentPage]  = useState(1);
  const [totalPages, setTotalPages]    = useState(1);
  const [totalItems, setTotalItems]    = useState(0);
  const [searchQuery, setSearchQuery]  = useState('');
  const [filterStatut, setFilterStatut]= useState('all');

  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [showDevisForm, setShowDevisForm] = useState(false);

  const [actionLoading, setActionLoading] = useState(null);

  // Formulaire devis
  const [devisForm, setDevisForm] = useState({
    montant_par_personne:'', montant_total:'',
    description_prestation:'', conditions_paiement:
      'Paiement à 30 jours à réception de facture.\nAcompte de 50% à la commande.',
    notes_internes:'', date_validite_devis:'',
  });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(() => {
    setLoadingStats(true);
    axios.get(`${API}/admin/devis/stats`, { headers })
      .then(({data}) => setStats(data))
      .finally(() => setLoadingStats(false));
  }, []);

  const fetchDevis = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/devis`, { headers, params:{
      page: currentPage, per_page: 12,
      statut: filterStatut !== 'all' ? filterStatut : undefined,
      search: searchQuery || undefined,
    }})
    .then(({data}) => {
      setDevisList(data.data);
      setTotalPages(data.last_page);
      setTotalItems(data.total);
    })
    .finally(() => setLoading(false));
  }, [currentPage, filterStatut, searchQuery]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchDevis(); }, [fetchDevis]);
  useEffect(() => { setCurrentPage(1); }, [filterStatut, searchQuery]);

  // Auto-calcul montant total
  useEffect(() => {
    const pp = parseFloat(devisForm.montant_par_personne) || 0;
    const n  = selectedDevis?.nombre_a_inscrire || 1;
    if (pp > 0) setDevisForm(f => ({ ...f, montant_total: (pp * n).toFixed(2) }));
  }, [devisForm.montant_par_personne, selectedDevis]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const doAction = async (key, fn) => {
    setActionLoading(key);
    try { await fn(); } finally { setActionLoading(null); }
  };

  const handleUpdateStatut = (id, statut) => doAction('statut-'+id, async () => {
    await axios.patch(`${API}/admin/devis/${id}/statut`, { statut }, { headers });
    addToast('Statut mis à jour');
    fetchDevis(); fetchStats();
    setShowModal(false);
  });

  const handleRejeter = (id) => doAction('rejeter-'+id, async () => {
    await axios.post(`${API}/admin/devis/${id}/rejeter`, {}, { headers });
    addToast('Demande rejetée', 'error');
    fetchDevis(); fetchStats();
    setShowModal(false);
  });

  const handleApprouver = (id) => doAction('approuver-'+id, async () => {
    await axios.post(`${API}/admin/devis/${id}/approuver`, {}, { headers });
    addToast('Devis approuvé ✓');
    fetchDevis(); fetchStats();
    setShowModal(false);
  });

  const handleSoumettre = async () => {
    if (!devisForm.montant_total || !devisForm.description_prestation || !devisForm.date_validite_devis) {
      addToast('Veuillez remplir tous les champs obligatoires', 'error'); return;
    }
    doAction('soumettre-'+selectedDevis.id, async () => {
      await axios.post(`${API}/admin/devis/${selectedDevis.id}/soumettre`, devisForm, { headers });
      addToast(isSuperAdmin ? 'Devis approuvé directement ✓' : 'Devis soumis pour approbation ✓');
      fetchDevis(); fetchStats();
      setShowDevisForm(false); setShowModal(false);
    });
  };

  const handleGenererPdf = (id) => doAction('pdf-'+id, async () => {
    const resp = await axios.get(`${API}/admin/devis/${id}/pdf`, {
      headers, responseType:'blob'
    });
    const url = URL.createObjectURL(new Blob([resp.data], {type:'application/pdf'}));
    const a   = document.createElement('a');
    a.href    = url;
    a.download= `Devis_CINEF_${id}.pdf`;
    a.click();
    addToast('PDF téléchargé ✓');
  });

  const handleEnvoyerClient = (id) => doAction('envoyer-'+id, async () => {
    await axios.post(`${API}/admin/devis/${id}/envoyer-client`, {}, { headers });
    addToast('Devis envoyé au client par email ✓');
    fetchDevis(); fetchStats();
    setShowModal(false);
  });

  const openDevisForm = (d) => {
    setSelectedDevis(d);
    setDevisForm({
      montant_par_personne: d.montant_par_personne || '',
      montant_total:        d.montant_total || '',
      description_prestation: d.description_prestation || '',
      conditions_paiement:    d.conditions_paiement || 'Paiement à 30 jours à réception de facture.\nAcompte de 50% à la commande.',
      notes_internes:         d.notes_internes || '',
      date_validite_devis:    d.date_validite_devis || '',
    });
    setShowDevisForm(true);
    setShowModal(false);
  };

  // ── Thème ─────────────────────────────────────────────────────────────────
  const C = isDark ? {
    page:'#070a12', panel:'#0c1020', panelBorder:'rgba(255,255,255,0.07)',
    card:'rgba(255,255,255,0.04)', cardBorder:'rgba(255,255,255,0.08)',
    surface:'rgba(255,255,255,0.05)', surfBorder:'rgba(255,255,255,0.09)',
    text:'#c8d4e0', textDim:'#4e6070', title:'#eef2f7',
    inputBg:'rgba(255,255,255,0.06)', inputBorder:'rgba(255,255,255,0.11)',
    shadow:'0 2px 20px rgba(0,0,0,0.5)', modalBg:'#0d1220',
    modalBorder:'rgba(255,255,255,0.1)', detailBg:'rgba(255,255,255,0.04)',
    detailBorder:'rgba(255,255,255,0.08)', filterBorder:'rgba(255,255,255,0.1)',
    heroBg:'rgba(255,255,255,0.03)', heroBorder:'rgba(255,255,255,0.07)',
  } : {
    page:'#eef1f7', panel:'#ffffff', panelBorder:'rgba(0,0,0,0.07)',
    card:'#ffffff', cardBorder:'rgba(0,0,0,0.07)',
    surface:'#f7f9fb', surfBorder:'rgba(0,0,0,0.07)',
    text:'#4d6070', textDim:'#9ab0c0', title:'#1a2535',
    inputBg:'#ffffff', inputBorder:'rgba(0,0,0,0.1)',
    shadow:'0 2px 16px rgba(0,0,0,0.07)', modalBg:'#ffffff',
    modalBorder:'rgba(0,0,0,0.08)', detailBg:'#f9fafb',
    detailBorder:'rgba(0,0,0,0.07)', filterBorder:'rgba(0,0,0,0.09)',
    heroBg:'#ffffff', heroBorder:'rgba(0,0,0,0.07)',
  };

  const mainML = `${SB_GAP+(sidebarOpen?SB_OPEN:SB_CLOSED)+12}px`;
  const mainPT = `${NB_TOP+NB_HEIGHT+12}px`;

  const STAT_CARDS = [
    { label:'Total',          key:'total',          accent:'#8fa3b5', icon:FaFileInvoice, strip:'linear-gradient(180deg,#fd7e14,#dc3545)' },
    { label:'En attente',     key:'en_attente',     accent:'#fd7e14', icon:FaClock,        strip:'linear-gradient(180deg,#fd7e14,#ffc107)' },
    { label:'En traitement',  key:'en_traitement',  accent:'#20c997', icon:FaHourglassHalf,strip:'linear-gradient(180deg,#20c997,#198754)' },
    { label:'En approbation', key:'en_approbation', accent:'#6f42c1', icon:FaHourglassHalf,strip:'linear-gradient(180deg,#6f42c1,#20c997)' },
    { label:'Approuvés',      key:'approuve',       accent:'#198754', icon:FaCheckCircle,  strip:'linear-gradient(180deg,#198754,#20c997)' },
    { label:'Envoyés',        key:'envoye',         accent:'#198754', icon:FaPaperPlane,   strip:'linear-gradient(180deg,#198754,#20c997)' },
    { label:'Rejetés',        key:'rejete',         accent:'#dc3545', icon:FaTimesCircle,  strip:'linear-gradient(180deg,#dc3545,#fd7e14)' },
  ];

  const FILTER_PILLS = [
    { id:'all',           label:'Tous' },
    { id:'nouveau',       label:'Nouveaux' },
    { id:'en_attente',    label:'En attente' },
    { id:'en_traitement', label:'En traitement' },
    { id:'en_approbation',label:'En approbation' },
    { id:'approuve',      label:'Approuvés' },
    { id:'envoye',        label:'Envoyés' },
    { id:'rejete',        label:'Rejetés' },
  ];

  const Spin = () => <FaSpinner style={{ animation:'dv-spin 0.8s linear infinite', fontSize:13 }} />;

  return (
    <div style={{ background:C.page, minHeight:'100vh', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;font-family:'Plus Jakarta Sans',sans-serif}
        @keyframes dv-shimmer{from{background-position:200% 0}to{background-position:-200% 0}}
        @keyframes dv-fade   {from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes dv-fo     {from{opacity:0}to{opacity:1}}
        @keyframes dv-up     {from{opacity:0;transform:translateY(24px) scale(0.97)}to{opacity:1;transform:none}}
        @keyframes dv-spin   {from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes dv-pulse  {0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}

        .dv-main{transition:margin-left 0.28s cubic-bezier(0.4,0,0.2,1);min-height:100vh}
        .dv-body{padding:22px 22px 40px;animation:dv-fade 0.4s ease}

        .dv-hero{border-radius:16px;padding:26px 30px;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;gap:20px;border:1px solid;position:relative;overflow:hidden}
        .dv-hero::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,#fd7e14 0%,#dc3545 50%,#198754 100%);border-radius:16px 16px 0 0}
        .dv-hero-left{display:flex;align-items:center;gap:16px}
        .dv-hero-icon{width:50px;height:50px;border-radius:13px;display:flex;align-items:center;justify-content:center;font-size:19px;color:#fd7e14;flex-shrink:0;border:1px solid rgba(253,126,20,0.25);background:rgba(253,126,20,0.08)}
        .dv-hero-eyebrow{font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#fd7e14;opacity:0.8;display:block;margin-bottom:3px}
        .dv-hero-title{font-size:21px;font-weight:700;letter-spacing:-0.3px}
        .dv-hero-sub{font-size:13.5px;font-weight:400;opacity:0.65;margin-top:2px}

        .dv-stats{display:grid;grid-template-columns:repeat(7,1fr);gap:10px;margin-bottom:20px}
        .dv-stat{border-radius:13px;border:1px solid;padding:16px 14px;display:flex;align-items:center;gap:12px;transition:transform 0.2s;cursor:default;position:relative;overflow:hidden}
        .dv-stat:hover{transform:translateY(-3px)}
        .dv-stat-icon{width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:15px;border:1px solid;flex-shrink:0}
        .dv-stat-num{font-size:22px;font-weight:700;letter-spacing:-0.5px;line-height:1}
        .dv-stat-label{font-size:11px;font-weight:500;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

        .dv-filters{border-radius:14px;border:1px solid;padding:14px 18px;margin-bottom:20px;display:flex;align-items:center;gap:10px;flex-wrap:wrap}
        .dv-search-wrap{flex:1;min-width:220px;position:relative;display:flex;align-items:center}
        .dv-search-ico{position:absolute;left:13px;font-size:13px;pointer-events:none;z-index:1}
        .dv-search-input{width:100%;height:42px;padding:0 14px 0 38px;border:1.5px solid;border-radius:10px;font-size:14px;outline:none;transition:border-color 0.2s,box-shadow 0.2s}
        .dv-search-input::placeholder{color:#9ab0c0}
        .dv-search-input:focus{border-color:#fd7e14!important;box-shadow:0 0 0 3px rgba(253,126,20,0.1)}
        .dv-pills{display:flex;gap:5px;flex-wrap:wrap}
        .dv-pill{display:flex;align-items:center;gap:5px;padding:0 12px;height:38px;border-radius:9px;border:1.5px solid;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;background:transparent}
        .dv-pill.active{background:#fd7e14;border-color:#fd7e14;color:#fff}

        .dv-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:16px}
        .dv-card{border-radius:16px;border:1px solid;overflow:hidden;transition:transform 0.22s,box-shadow 0.22s;display:flex;flex-direction:column}
        .dv-card:hover{transform:translateY(-4px)}
        .dv-card-strip{height:3px;width:100%;flex-shrink:0}
        .dv-card-inner{padding:18px;display:flex;flex-direction:column;gap:14px;flex:1}
        .dv-card-head{display:flex;align-items:flex-start;gap:12px}
        .dv-card-av{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:white;flex-shrink:0}
        .dv-card-name{font-size:15px;font-weight:700;margin-bottom:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .dv-card-company{font-size:12px;font-weight:500;opacity:0.55}
        .dv-urgence{width:30px;height:30px;border-radius:8px;background:rgba(220,53,69,0.1);color:#dc3545;display:flex;align-items:center;justify-content:center;font-size:13px;animation:dv-pulse 2s ease-in-out infinite}
        .dv-card-infos{display:flex;flex-direction:column;gap:8px}
        .dv-info-row{display:flex;align-items:center;gap:9px;padding:9px 11px;border-radius:9px;border:1px solid}
        .dv-info-lbl{font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;opacity:0.45;display:block;margin-bottom:1px}
        .dv-info-val{font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .dv-info-split{display:grid;grid-template-columns:1fr 1fr;gap:8px}
        .dv-card-foot{padding:13px 18px;display:flex;align-items:center;justify-content:space-between;border-top:1px solid;gap:8px}
        .dv-status-badge{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:20px;border:1.5px solid;font-size:12.5px;font-weight:600}
        .dv-view-btn{display:flex;align-items:center;gap:6px;padding:7px 14px;border:1.5px solid rgba(253,126,20,0.3);background:rgba(253,126,20,0.06);color:#fd7e14;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.18s}
        .dv-view-btn:hover{background:rgba(253,126,20,0.12);border-color:#fd7e14}

        .dv-pager{display:flex;align-items:center;gap:6px;padding:20px 0 4px;flex-wrap:wrap}
        .dv-pg{min-width:38px;height:38px;border-radius:9px;border:1.5px solid;background:transparent;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0 10px;transition:all 0.18s}
        .dv-pg:hover:not(:disabled){border-color:#fd7e14;color:#fd7e14}
        .dv-pg.on{background:#fd7e14;color:#fff;border-color:#fd7e14}
        .dv-pg:disabled{opacity:0.28;cursor:not-allowed}

        .dv-empty{grid-column:1/-1;display:flex;flex-direction:column;align-items:center;gap:14px;padding:72px 20px;text-align:center}
        .dv-empty-ico{width:72px;height:72px;border-radius:20px;display:flex;align-items:center;justify-content:center;font-size:28px;color:#fd7e14;background:rgba(253,126,20,0.07);border:1px solid rgba(253,126,20,0.14)}

        .dv-overlay{position:fixed;inset:0;background:rgba(4,7,16,0.7);backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;z-index:3000;padding:20px;animation:dv-fo 0.2s ease}
        .dv-modal{width:100%;max-width:700px;max-height:92vh;overflow-y:auto;border-radius:20px;border:1px solid;animation:dv-up 0.26s cubic-bezier(0.34,1.56,0.64,1);scrollbar-width:thin}
        .dv-modal::-webkit-scrollbar{width:4px}
        .dv-modal::-webkit-scrollbar-track{background:transparent}
        .dv-modal::-webkit-scrollbar-thumb{background:rgba(253,126,20,0.3);border-radius:2px}
        .dv-modal-head{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid;gap:14px;position:relative;overflow:hidden}
        .dv-modal-head::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:linear-gradient(180deg,#fd7e14 0%,#dc3545 100%)}
        .dv-modal-av{width:46px;height:46px;border-radius:12px;background:rgba(253,126,20,0.1);color:#fd7e14;border:1.5px solid rgba(253,126,20,0.25);font-size:16px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .dv-modal-x{width:36px;height:36px;border-radius:9px;border:1.5px solid;background:transparent;display:flex;align-items:center;justify-content:center;font-size:14px;cursor:pointer;transition:all 0.18s;flex-shrink:0;padding:0}
        .dv-modal-x:hover{background:rgba(220,53,69,0.08);border-color:#dc3545;color:#dc3545}
        .dv-modal-body{padding:22px 24px;display:flex;flex-direction:column;gap:18px}
        .dv-sect{font-size:9.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#fd7e14;margin-bottom:12px;display:flex;align-items:center;gap:10px}
        .dv-sect::after{content:'';flex:1;height:1px;background:rgba(253,126,20,0.18)}
        .dv-info-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}
        .dv-info-grid .full{grid-column:1/-1}
        .dv-detail-item{display:flex;gap:11px;padding:12px 14px;border-radius:11px;border:1px solid}
        .dv-detail-lbl{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#8fa3b5;display:block;margin-bottom:3px}
        .dv-detail-val{font-size:14px;font-weight:600;display:block}
        .dv-msg-box{display:flex;gap:12px;padding:14px 16px;border-radius:11px;border:1px solid;border-left:3px solid #fd7e14}
        .dv-modal-foot{display:flex;gap:8px;padding:16px 24px;border-top:1px solid;flex-wrap:wrap}
        .dv-mbt{flex:1;min-width:100px;display:flex;align-items:center;justify-content:center;gap:7px;padding:11px 14px;border-radius:10px;border:1.5px solid;font-size:13.5px;font-weight:600;cursor:pointer;transition:all 0.2s}
        .dv-mbt:disabled{opacity:0.45;cursor:not-allowed}
        .dv-mbt.close{background:transparent}
        .dv-mbt.close:hover{background:rgba(0,0,0,0.05)}
        .dv-mbt.reject{color:#dc3545;border-color:rgba(220,53,69,0.35);background:transparent}
        .dv-mbt.reject:hover:not(:disabled){background:#dc3545;color:#fff;border-color:#dc3545}
        .dv-mbt.approve{color:#198754;border-color:rgba(25,135,84,0.35);background:transparent}
        .dv-mbt.approve:hover:not(:disabled){background:#198754;color:#fff;border-color:#198754}
        .dv-mbt.primary{background:linear-gradient(135deg,#fd7e14,#dc3545);color:#fff;border-color:transparent;box-shadow:0 4px 14px rgba(253,126,20,0.25)}
        .dv-mbt.primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 6px 20px rgba(253,126,20,0.35)}
        .dv-mbt.send-client{background:linear-gradient(135deg,#198754,#20c997);color:#fff;border-color:transparent;box-shadow:0 4px 14px rgba(25,135,84,0.25)}
        .dv-mbt.send-client:hover:not(:disabled){transform:translateY(-1px)}

        .dv-form-label{font-size:12px;font-weight:700;margin-bottom:6px;display:block}
        .dv-form-input{width:100%;padding:11px 14px;border:1.5px solid;border-radius:10px;font-size:14px;outline:none;transition:all 0.2s;font-family:'Plus Jakarta Sans',sans-serif}
        .dv-form-input:focus{border-color:#fd7e14!important;box-shadow:0 0 0 3px rgba(253,126,20,0.1)}
        .dv-form-textarea{width:100%;padding:11px 14px;border:1.5px solid;border-radius:10px;font-size:13px;outline:none;transition:all 0.2s;resize:vertical;min-height:90px;font-family:'Plus Jakarta Sans',sans-serif;line-height:1.6}
        .dv-form-textarea:focus{border-color:#fd7e14!important;box-shadow:0 0 0 3px rgba(253,126,20,0.1)}
        .dv-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
        .dv-form-grid .full{grid-column:1/-1}
        .dv-total-preview{background:linear-gradient(135deg,rgba(253,126,20,0.08),rgba(253,126,20,0.04));border:1px solid rgba(253,126,20,0.2);border-radius:11px;padding:14px 18px;display:flex;align-items:center;justify-content:space-between}

        @media(max-width:1400px){.dv-stats{grid-template-columns:repeat(4,1fr)}}
        @media(max-width:1024px){.dv-main{margin-left:0!important}.dv-stats{grid-template-columns:repeat(3,1fr)}}
        @media(max-width:768px){.dv-stats{grid-template-columns:repeat(2,1fr)}.dv-body{padding:14px 14px 32px}.dv-info-grid{grid-template-columns:1fr}.dv-form-grid{grid-template-columns:1fr}}
        @media(max-width:480px){.dv-stats{grid-template-columns:1fr}.dv-grid{grid-template-columns:1fr}}
      `}</style>

      <Toast toasts={toasts} remove={removeToast} />
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar  sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Demandes de Devis" />

      <main className="dv-main" style={{ marginLeft:mainML, paddingTop:mainPT }}>
        <div className="dv-body">

          {/* ── HERO ── */}
          <div className="dv-hero" style={{ background:C.heroBg, borderColor:C.heroBorder }}>
            <div className="dv-hero-left">
              <div className="dv-hero-icon"><FaFileInvoice /></div>
              <div>
                <span className="dv-hero-eyebrow">Gestion</span>
                <div className="dv-hero-title" style={{ color:C.title }}>Demandes de Devis</div>
                <div className="dv-hero-sub" style={{ color:C.text }}>Traitez, approuvez et envoyez vos devis en quelques clics</div>
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <span style={{ fontSize:13, fontWeight:600, color:C.textDim, padding:'9px 14px', borderRadius:10, border:`1px solid ${C.panelBorder}`, display:'flex', alignItems:'center', gap:6 }}>
                <FaUsers style={{ fontSize:11 }} /> {totalItems} demande{totalItems!==1?'s':''}
              </span>
            </div>
          </div>

          {/* ── STATS ── */}
          <div className="dv-stats">
            {STAT_CARDS.map((s,i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="dv-stat" style={{ background:C.card, borderColor:C.cardBorder, boxShadow:C.shadow }}>
                  <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, borderRadius:'13px 0 0 13px', background:s.strip }} />
                  <div className="dv-stat-icon" style={{ background:`${s.accent}12`, color:s.accent, borderColor:`${s.accent}22` }}>
                    {loadingStats ? <Spin /> : <Icon />}
                  </div>
                  <div>
                    <div className="dv-stat-num" style={{ color:s.accent }}>{loadingStats ? '…' : (stats[s.key] ?? 0)}</div>
                    <div className="dv-stat-label" style={{ color:C.textDim }}>{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── FILTRES ── */}
          <div className="dv-filters" style={{ background:C.panel, borderColor:C.panelBorder, boxShadow:C.shadow }}>
            <div className="dv-search-wrap">
              <FaSearch className="dv-search-ico" style={{ color:C.textDim }} />
              <input type="text" className="dv-search-input" placeholder="Rechercher nom, email, entreprise…"
                value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
                style={{ background:C.inputBg, borderColor:C.inputBorder, color:C.title }} />
            </div>
            <div className="dv-pills">
              {FILTER_PILLS.map(f => {
                const st = STATUTS[f.id];
                const isOn = filterStatut === f.id;
                return (
                  <button key={f.id} className={`dv-pill${isOn?' active':''}`}
                    onClick={() => setFilterStatut(f.id)}
                    style={{
                      borderColor: isOn ? '#fd7e14' : C.filterBorder,
                      color: isOn ? '#fff' : (st?.color || C.text),
                    }}>
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── CARDS ── */}
          <div className="dv-grid">
            {loading
              ? [...Array(6)].map((_,i) => <Sk key={i} h={280} />)
              : devisList.length === 0
                ? <div className="dv-empty">
                    <div className="dv-empty-ico"><FaFileInvoice /></div>
                    <h3 style={{ color:C.title, fontSize:19, fontWeight:700 }}>Aucune demande trouvée</h3>
                    <p style={{ color:C.textDim, fontSize:14 }}>Essayez de modifier vos critères</p>
                  </div>
                : devisList.map(d => {
                    const st  = STATUTS[d.statut] || STATUTS.nouveau;
                    const tc  = TYPE_COLORS[d.type_formation] || { color:'#8fa3b5', bg:'rgba(143,163,181,0.1)', border:'rgba(143,163,181,0.2)' };
                    const avC = d.statut==='approuve'||d.statut==='envoye' ? '#198754' : d.statut==='rejete' ? '#dc3545' : '#fd7e14';
                    return (
                      <div key={d.id} className="dv-card" style={{ background:C.card, borderColor:C.cardBorder, boxShadow:C.shadow }}
                        onMouseEnter={e=>{e.currentTarget.style.boxShadow=`0 10px 36px rgba(0,0,0,${isDark?'0.5':'0.12'})`;e.currentTarget.style.borderColor=st.border}}
                        onMouseLeave={e=>{e.currentTarget.style.boxShadow=C.shadow;e.currentTarget.style.borderColor=C.cardBorder}}>
                        <div className="dv-card-strip" style={{ background:`linear-gradient(90deg,${st.color},${st.color}66)` }} />
                        <div className="dv-card-inner">
                          <div className="dv-card-head">
                            <div className="dv-card-av" style={{ background:avC }}>{d.prenoms?.[0]}{d.nom?.[0]}</div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div className="dv-card-name" style={{ color:C.title }}>{d.prenoms} {d.nom}</div>
                              <div className="dv-card-company" style={{ color:C.text }}>{d.entreprise || d.email}</div>
                            </div>
                          </div>
                          <div className="dv-card-infos">
                            <div className="dv-info-row" style={{ background:C.surface, borderColor:C.surfBorder }}>
                              <FaGraduationCap style={{ color:'#fd7e14', fontSize:13 }} />
                              <div style={{ flex:1, minWidth:0 }}>
                                <span className="dv-info-lbl" style={{ color:C.textDim }}>Formation</span>
                                <span className="dv-info-val" style={{ color:C.title }}>{d.formation_specifique || d.domaine}</span>
                              </div>
                            </div>
                            <div className="dv-info-split">
                              <div className="dv-info-row" style={{ background:C.surface, borderColor:C.surfBorder }}>
                                <FaMapMarkerAlt style={{ color:'#20c997', fontSize:13 }} />
                                <div style={{ flex:1, minWidth:0 }}>
                                  <span className="dv-info-lbl" style={{ color:C.textDim }}>Ville</span>
                                  <span className="dv-info-val" style={{ color:C.title }}>{d.ville}</span>
                                </div>
                              </div>
                              <div className="dv-info-row" style={{ background:C.surface, borderColor:C.surfBorder }}>
                                <FaUsers style={{ color:'#198754', fontSize:13 }} />
                                <div style={{ flex:1, minWidth:0 }}>
                                  <span className="dv-info-lbl" style={{ color:C.textDim }}>Participants</span>
                                  <span className="dv-info-val" style={{ color:C.title }}>{d.nombre_a_inscrire}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                            <span style={{ display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius:6, border:'1px solid', fontSize:12, fontWeight:600, color:tc.color, background:tc.bg, borderColor:tc.border }}>
                              {TYPE_LABELS[d.type_formation] || d.type_formation}
                            </span>
                            {d.montant_total && (
                              <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:700, color:'#198754', background:'rgba(25,135,84,0.08)', border:'1px solid rgba(25,135,84,0.2)', padding:'4px 10px', borderRadius:6 }}>
                                <FaEuroSign style={{ fontSize:10 }} />{fmtMoney(d.montant_total)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="dv-card-foot" style={{ borderTopColor:C.cardBorder, background:isDark?'rgba(255,255,255,0.02)':'rgba(0,0,0,0.015)' }}>
                          <span className="dv-status-badge" style={{ color:st.color, background:st.bg, borderColor:st.border }}>
                            <st.icon style={{ fontSize:10 }} />{st.label}
                          </span>
                          <button className="dv-view-btn" onClick={() => { setSelectedDevis(d); setShowModal(true); }}>
                            <FaEye style={{ fontSize:12 }} /> Détails
                          </button>
                        </div>
                      </div>
                    );
                  })
            }
          </div>

          {/* ── PAGINATION ── */}
          {totalPages > 1 && (
            <div className="dv-pager">
              <button className="dv-pg" onClick={()=>setCurrentPage(1)} disabled={currentPage===1} style={{ borderColor:C.panelBorder, color:C.textDim }}>«</button>
              <button className="dv-pg" onClick={()=>setCurrentPage(p=>Math.max(p-1,1))} disabled={currentPage===1} style={{ borderColor:C.panelBorder, color:C.textDim }}>Précédent</button>
              {[...Array(totalPages)].map((_,i) => (
                <button key={i} className={`dv-pg${currentPage===i+1?' on':''}`}
                  onClick={()=>setCurrentPage(i+1)}
                  style={{ borderColor:currentPage===i+1?'#fd7e14':C.panelBorder, color:currentPage===i+1?'#fff':C.textDim }}>
                  {i+1}
                </button>
              ))}
              <button className="dv-pg" onClick={()=>setCurrentPage(p=>Math.min(p+1,totalPages))} disabled={currentPage===totalPages} style={{ borderColor:C.panelBorder, color:C.textDim }}>Suivant</button>
              <button className="dv-pg" onClick={()=>setCurrentPage(totalPages)} disabled={currentPage===totalPages} style={{ borderColor:C.panelBorder, color:C.textDim }}>»</button>
              <span style={{ marginLeft:'auto', fontSize:13, color:C.textDim, fontWeight:500 }}>
                Page {currentPage}/{totalPages} · {totalItems} demande{totalItems!==1?'s':''}
              </span>
            </div>
          )}
        </div>
      </main>

      {/* ── MODAL DÉTAILS ── */}
      {showModal && selectedDevis && (
        <div className="dv-overlay" onClick={()=>setShowModal(false)}>
          <div className="dv-modal" style={{ background:C.modalBg, borderColor:C.modalBorder }} onClick={e=>e.stopPropagation()}>
            <div className="dv-modal-head" style={{ borderBottomColor:C.modalBorder }}>
              <div style={{ display:'flex', alignItems:'center', gap:13 }}>
                <div className="dv-modal-av">{selectedDevis.prenoms?.[0]}{selectedDevis.nom?.[0]}</div>
                <div>
                  <div style={{ fontSize:17, fontWeight:700, color:C.title, marginBottom:2 }}>Demande #{selectedDevis.id}</div>
                  <div style={{ fontSize:13, color:C.textDim }}>{selectedDevis.prenoms} {selectedDevis.nom} · {selectedDevis.entreprise || selectedDevis.email}</div>
                </div>
              </div>
              <button className="dv-modal-x" onClick={()=>setShowModal(false)} style={{ borderColor:C.modalBorder, color:C.textDim }}><FaTimes /></button>
            </div>

            <div className="dv-modal-body">
              {/* Statut */}
              {(() => { const st = STATUTS[selectedDevis.statut] || STATUTS.nouveau; return (
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <span className="dv-status-badge" style={{ color:st.color, background:st.bg, borderColor:st.border }}>
                    <st.icon style={{ fontSize:10 }} />{st.label}
                  </span>
                  <span style={{ fontSize:12, color:C.textDim }}>{fmtDate(selectedDevis.created_at)}</span>
                  {selectedDevis.traite_par && <span style={{ fontSize:12, color:C.textDim }}>Traité par : {selectedDevis.traite_par}</span>}
                  {selectedDevis.approuve_par && <span style={{ fontSize:12, color:'#198754' }}>Approuvé par : {selectedDevis.approuve_par}</span>}
                </div>
              ); })()}

              {/* Infos client */}
              <div>
                <div className="dv-sect">Informations Client</div>
                <div className="dv-info-grid">
                  {[
                    { label:'Nom complet', val:`${selectedDevis.prenoms} ${selectedDevis.nom}`, icon:FaUser,       color:'#fd7e14' },
                    { label:'Email',       val:selectedDevis.email,                             icon:FaEnvelope,   color:'#20c997' },
                    { label:'Téléphone',   val:selectedDevis.telephone,                         icon:FaPhone,      color:'#198754' },
                    { label:'Entreprise',  val:selectedDevis.entreprise||'—',                   icon:FaBriefcase,  color:'#fd7e14' },
                  ].map((d,i) => (
                    <div key={i} className="dv-detail-item" style={{ background:C.detailBg, borderColor:C.detailBorder }}>
                      <d.icon style={{ color:d.color, fontSize:15, flexShrink:0, marginTop:2 }} />
                      <div><span className="dv-detail-lbl">{d.label}</span><span className="dv-detail-val" style={{ color:C.title }}>{d.val}</span></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Infos formation */}
              <div>
                <div className="dv-sect">Détails Formation</div>
                <div className="dv-info-grid">
                  <div className="dv-detail-item full" style={{ background:C.detailBg, borderColor:C.detailBorder }}>
                    <FaGraduationCap style={{ color:'#fd7e14', fontSize:15, flexShrink:0, marginTop:2 }} />
                    <div><span className="dv-detail-lbl">Formation demandée</span><span className="dv-detail-val" style={{ color:C.title }}>{selectedDevis.domaine}{selectedDevis.formation_specifique?' — '+selectedDevis.formation_specifique:''}</span></div>
                  </div>
                  {[
                    { label:'Type',         val:TYPE_LABELS[selectedDevis.type_formation]||selectedDevis.type_formation, icon:FaFileInvoice,  color:'#20c997' },
                    { label:'Participants', val:`${selectedDevis.nombre_a_inscrire} personnes`,                          icon:FaUsers,        color:'#198754' },
                    { label:'Date début',   val:fmtDate(selectedDevis.date_debut_souhaitee),                             icon:FaCalendarAlt,  color:'#fd7e14' },
                    { label:'Ville',        val:selectedDevis.ville,                                                     icon:FaMapMarkerAlt, color:'#20c997' },
                  ].map((d,i) => (
                    <div key={i} className="dv-detail-item" style={{ background:C.detailBg, borderColor:C.detailBorder }}>
                      <d.icon style={{ color:d.color, fontSize:15, flexShrink:0, marginTop:2 }} />
                      <div><span className="dv-detail-lbl">{d.label}</span><span className="dv-detail-val" style={{ color:C.title }}>{d.val}</span></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Devis existant */}
              {selectedDevis.montant_total && (
                <div>
                  <div className="dv-sect">Devis Rempli</div>
                  <div className="dv-total-preview">
                    <div>
                      <p style={{ fontSize:11, color:C.textDim, marginBottom:3 }}>Montant total</p>
                      <p style={{ fontSize:22, fontWeight:700, color:'#198754' }}>{fmtMoney(selectedDevis.montant_total)}</p>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <p style={{ fontSize:11, color:C.textDim, marginBottom:3 }}>Par personne</p>
                      <p style={{ fontSize:16, fontWeight:600, color:C.title }}>{fmtMoney(selectedDevis.montant_par_personne)}</p>
                    </div>
                    {selectedDevis.date_validite_devis && (
                      <div style={{ textAlign:'right' }}>
                        <p style={{ fontSize:11, color:C.textDim, marginBottom:3 }}>Valide jusqu'au</p>
                        <p style={{ fontSize:14, fontWeight:600, color:C.title }}>{fmtDate(selectedDevis.date_validite_devis)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Message */}
              {selectedDevis.message && (
                <div>
                  <div className="dv-sect">Message du Client</div>
                  <div className="dv-msg-box" style={{ background:C.detailBg, borderColor:C.detailBorder }}>
                    <FaCommentAlt style={{ color:'#fd7e14', fontSize:14, flexShrink:0, marginTop:2 }} />
                    <p style={{ color:C.text, fontSize:14, lineHeight:1.65 }}>{selectedDevis.message}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer actions selon statut et rôle */}
            <div className="dv-modal-foot" style={{ borderTopColor:C.modalBorder }}>
              <button className="dv-mbt close" onClick={()=>setShowModal(false)} style={{ borderColor:C.filterBorder, color:C.text }}>Fermer</button>

              {/* Rejeter (tous sauf envoye) */}
              {!['envoye','rejete'].includes(selectedDevis.statut) && (
                <button className="dv-mbt reject" disabled={!!actionLoading}
                  onClick={()=>handleRejeter(selectedDevis.id)}>
                  {actionLoading===`rejeter-${selectedDevis.id}` ? <Spin /> : <FaTimesCircle />} Rejeter
                </button>
              )}

              {/* Passer en attente si nouveau */}
              {selectedDevis.statut === 'nouveau' && (
                <button className="dv-mbt approve" disabled={!!actionLoading}
                  onClick={()=>handleUpdateStatut(selectedDevis.id,'en_attente')}>
                  {actionLoading===`statut-${selectedDevis.id}` ? <Spin /> : <FaClock />} Prendre en charge
                </button>
              )}

              {/* Remplir le formulaire devis */}
              {['en_attente','en_traitement'].includes(selectedDevis.statut) && (
                <button className="dv-mbt primary" onClick={()=>openDevisForm(selectedDevis)}>
                  <FaFileInvoice /> Remplir le devis
                </button>
              )}

              {/* Approuver (super_admin uniquement, statut en_approbation) */}
              {isSuperAdmin && selectedDevis.statut === 'en_approbation' && (
                <button className="dv-mbt approve" disabled={!!actionLoading}
                  onClick={()=>handleApprouver(selectedDevis.id)}>
                  {actionLoading===`approuver-${selectedDevis.id}` ? <Spin /> : <FaThumbsUp />} Approuver
                </button>
              )}

              {/* Télécharger PDF */}
              {selectedDevis.statut === 'approuve' && (
                <button className="dv-mbt approve" disabled={!!actionLoading}
                  onClick={()=>handleGenererPdf(selectedDevis.id)}>
                  {actionLoading===`pdf-${selectedDevis.id}` ? <Spin /> : <FaFilePdf />} Télécharger PDF
                </button>
              )}

              {/* Envoyer au client */}
              {selectedDevis.statut === 'approuve' && (
                <button className="dv-mbt send-client" disabled={!!actionLoading}
                  onClick={()=>handleEnvoyerClient(selectedDevis.id)}>
                  {actionLoading===`envoyer-${selectedDevis.id}` ? <Spin /> : <FaPaperPlane />} Envoyer au client
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL FORMULAIRE DEVIS ── */}
      {showDevisForm && selectedDevis && (
        <div className="dv-overlay" onClick={()=>setShowDevisForm(false)}>
          <div className="dv-modal" style={{ background:C.modalBg, borderColor:C.modalBorder, maxWidth:640 }} onClick={e=>e.stopPropagation()}>
            <div className="dv-modal-head" style={{ borderBottomColor:C.modalBorder }}>
              <div style={{ display:'flex', alignItems:'center', gap:13 }}>
                <div style={{ width:46, height:46, borderRadius:12, background:'rgba(25,135,84,0.1)', color:'#198754', border:'1.5px solid rgba(25,135,84,0.25)', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <FaFileInvoice />
                </div>
                <div>
                  <div style={{ fontSize:17, fontWeight:700, color:C.title, marginBottom:2 }}>Formulaire Devis</div>
                  <div style={{ fontSize:13, color:C.textDim }}>{selectedDevis.prenoms} {selectedDevis.nom} · {selectedDevis.nombre_a_inscrire} personnes</div>
                </div>
              </div>
              <button className="dv-modal-x" onClick={()=>setShowDevisForm(false)} style={{ borderColor:C.modalBorder, color:C.textDim }}><FaTimes /></button>
            </div>

            <div className="dv-modal-body">
              {/* Résumé client */}
              <div style={{ background:'rgba(253,126,20,0.04)', border:'1px solid rgba(253,126,20,0.15)', borderRadius:11, padding:'12px 16px', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
                {[
                  { label:'Client',       val:`${selectedDevis.prenoms} ${selectedDevis.nom}` },
                  { label:'Entreprise',   val:selectedDevis.entreprise||'—'                   },
                  { label:'Formation',    val:selectedDevis.formation_specifique||selectedDevis.domaine },
                  { label:'Participants', val:`${selectedDevis.nombre_a_inscrire} pers.`      },
                  { label:'Type',         val:TYPE_LABELS[selectedDevis.type_formation]||selectedDevis.type_formation },
                  { label:'Ville',        val:selectedDevis.ville                              },
                ].map((r,i) => (
                  <div key={i}>
                    <p style={{ fontSize:9, fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#fd7e14', marginBottom:2 }}>{r.label}</p>
                    <p style={{ fontSize:13, fontWeight:600, color:C.title }}>{r.val}</p>
                  </div>
                ))}
              </div>

              {/* Montants */}
              <div>
                <div className="dv-sect">Tarification (€)</div>
                <div className="dv-form-grid">
                  <div>
                    <label className="dv-form-label" style={{ color:C.text }}>Prix par personne (€) *</label>
                    <input type="number" className="dv-form-input" placeholder="ex: 500"
                      value={devisForm.montant_par_personne}
                      onChange={e=>setDevisForm(f=>({...f,montant_par_personne:e.target.value}))}
                      style={{ background:C.inputBg, borderColor:C.inputBorder, color:C.title }} />
                  </div>
                  <div>
                    <label className="dv-form-label" style={{ color:C.text }}>Montant total (€) *</label>
                    <input type="number" className="dv-form-input"
                      value={devisForm.montant_total}
                      onChange={e=>setDevisForm(f=>({...f,montant_total:e.target.value}))}
                      style={{ background:C.inputBg, borderColor:C.inputBorder, color:C.title }} />
                  </div>
                </div>
                {devisForm.montant_total && (
                  <div className="dv-total-preview" style={{ marginTop:12 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:C.textDim }}>Total calculé</span>
                    <span style={{ fontSize:22, fontWeight:700, color:'#198754' }}>{fmtMoney(devisForm.montant_total)}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <div className="dv-sect">Description de la Prestation</div>
                <div className="dv-form-grid">
                  <div className="full">
                    <label className="dv-form-label" style={{ color:C.text }}>Description *</label>
                    <textarea className="dv-form-textarea" placeholder="Décrivez la prestation en détail…"
                      value={devisForm.description_prestation}
                      onChange={e=>setDevisForm(f=>({...f,description_prestation:e.target.value}))}
                      style={{ background:C.inputBg, borderColor:C.inputBorder, color:C.title }} />
                  </div>
                  <div className="full">
                    <label className="dv-form-label" style={{ color:C.text }}>Conditions de paiement</label>
                    <textarea className="dv-form-textarea" rows={3}
                      value={devisForm.conditions_paiement}
                      onChange={e=>setDevisForm(f=>({...f,conditions_paiement:e.target.value}))}
                      style={{ background:C.inputBg, borderColor:C.inputBorder, color:C.title }} />
                  </div>
                  <div>
                    <label className="dv-form-label" style={{ color:C.text }}>Date de validité *</label>
                    <input type="date" className="dv-form-input"
                      value={devisForm.date_validite_devis}
                      onChange={e=>setDevisForm(f=>({...f,date_validite_devis:e.target.value}))}
                      style={{ background:C.inputBg, borderColor:C.inputBorder, color:C.title }} />
                  </div>
                  <div>
                    <label className="dv-form-label" style={{ color:C.text }}>Notes internes (non visibles client)</label>
                    <textarea className="dv-form-textarea" rows={2} placeholder="Notes pour l'équipe…"
                      value={devisForm.notes_internes}
                      onChange={e=>setDevisForm(f=>({...f,notes_internes:e.target.value}))}
                      style={{ background:C.inputBg, borderColor:C.inputBorder, color:C.title }} />
                  </div>
                </div>
              </div>

              {/* Info workflow */}
              <div style={{ background:isSuperAdmin?'rgba(25,135,84,0.06)':'rgba(111,66,193,0.06)', border:`1px solid ${isSuperAdmin?'rgba(25,135,84,0.2)':'rgba(111,66,193,0.2)'}`, borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:16, color:isSuperAdmin?'#198754':'#6f42c1' }}>{isSuperAdmin?<FaCheck/>:<FaHourglassHalf/>}</span>
                <p style={{ fontSize:13, color:isSuperAdmin?'#198754':'#6f42c1', fontWeight:500 }}>
                  {isSuperAdmin
                    ? 'En tant que super administrateur, le devis sera approuvé directement.'
                    : 'Ce devis sera soumis au super administrateur pour approbation avant envoi.'}
                </p>
              </div>
            </div>

            <div className="dv-modal-foot" style={{ borderTopColor:C.modalBorder }}>
              <button className="dv-mbt close" onClick={()=>setShowDevisForm(false)} style={{ borderColor:C.filterBorder, color:C.text }}>Annuler</button>
              <button className="dv-mbt primary" disabled={!!actionLoading} onClick={handleSoumettre}>
                {actionLoading===`soumettre-${selectedDevis.id}` ? <><Spin /> Envoi…</> : <><FaFilePdf /> {isSuperAdmin?'Approuver & Générer':'Soumettre pour approbation'}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devis;